/**
/**
 * Register Page Component
 */

import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import FingerprintJS from '@fingerprintjs/fingerprintjs';
import { authAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import '../styles/Auth.css';

const Register = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [fullName, setFullName] = useState('');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [referralCode, setReferralCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [fingerprint, setFingerprint] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const location = useLocation();

  // If the user was trying to access a protected page before being sent to login/register,
  // preserve that destination so it can be restored after they verify their email and log in.
  const from = location.state?.from;

  // Save redirect location to localStorage if present in location.state
  useEffect(() => {
    if (from) {
      const dest = `${from.pathname}${from.search || ''}${from.hash || ''}`;
      try {
        localStorage.setItem('wikigame:redirect-after-login', dest);
      } catch (e) {
        console.error('Failed to save redirect destination:', e);
      }
    }
  }, [from]);

  // Redirect to original destination once logged in
  useEffect(() => {
    if (user) {
      const savedRedirect = localStorage.getItem('wikigame:redirect-after-login');
      if (savedRedirect) {
        localStorage.removeItem('wikigame:redirect-after-login');
        navigate(savedRedirect, { replace: true });
      } else {
        navigate('/dashboard', { replace: true });
      }
    }
  }, [user, navigate]);

  // Collect device fingerprint silently on mount
  useEffect(() => {
    FingerprintJS.load()
      .then(fp => fp.get())
      .then(result => setFingerprint(result.visitorId))
      .catch(() => {}); // non-blocking — registration proceeds without it
  }, []);

  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    const code = String(params.get('ref') || '').trim();
    if (code) setReferralCode(code);
  }, [location.search]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (!agreedToTerms) {
        setError('You must agree to the Terms & Conditions.');
        setLoading(false);
        return;
      }

      // Add phone to registration API call (update backend as needed)
      const response = await authAPI.register(
        String(fullName).trim(),
        String(username).trim(),
        String(email).trim(),
        String(phone).trim(),
        password,
        confirmPassword,
        String(referralCode).trim(),
        fingerprint
      );
      const sent = Boolean(response?.data?.verificationEmailSent);
      const apiMessage = String(response?.data?.message || '').trim();
      const localLink = String(response?.data?.devVerificationLink || '').trim();

      navigate(`/verify-email-sent?email=${encodeURIComponent(String(email).trim())}`, {
        state: { verificationEmailSent: sent, message: apiMessage, devVerificationLink: localLink, from },
        replace: true,
      });
    } catch (err) {
      const status = err?.response?.status;
      const data = err?.response?.data;

      let details = '';
      if (data && typeof data === 'object' && data.error) {
        details = data.error;
      } else if (typeof data === 'string' && data.trim()) {
        details = data.slice(0, 200);
      } else if (data) {
        try {
          details = JSON.stringify(data);
        } catch (_) {
          details = String(data);
        }
      } else {
        details = err?.message || 'Registration failed';
      }

      setError(`${status ? `HTTP ${status}: ` : ''}${details}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <h1>Wiki Game</h1>
        <h2>Create Account</h2>

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit}>

          <input
            type="text"
            placeholder="Full Name"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            autoComplete="name"
            required
          />

          <input
            type="text"
            placeholder="Username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoComplete="username"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            required
          />

          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            required
          />

          <input
            type="tel"
            placeholder="Phone Number"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            autoComplete="tel"
            required
          />

          <input
            type="password"
            placeholder="Password (min 6 characters)"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            required
          />

          <input
            type="password"
            placeholder="Confirm Password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            autoComplete="new-password"
            required
          />

          <input
            type="text"
            placeholder="Referral Code (optional)"
            value={referralCode}
            onChange={(e) => setReferralCode(e.target.value)}
            autoCapitalize="characters"
            autoCorrect="off"
            spellCheck={false}
          />

          <div className="terms-checkbox-row">
            <label>
              <input
                type="checkbox"
                checked={agreedToTerms}
                onChange={(e) => setAgreedToTerms(e.target.checked)}
              />
              {' '}I have read and agree to the <Link to="/terms" target="_blank" rel="noopener noreferrer">Terms &amp; Conditions</Link>
            </label>
          </div>

          <button type="submit" disabled={loading}>
            {loading ? 'Creating account...' : 'Register'}
          </button>
        </form>
        <p>
          Already have an account? <Link to="/login" state={{ from }}>Login here</Link>
        </p>
      </div>
    </div>
  );
};

export default Register;
